import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';

interface Language {
  _id: string;
  name: string;
  code: string;
  nativeName: string;
  flag?: string;
  isDefault: boolean;
  isActive: boolean;
  isRTL?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const LanguageManagement: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    nativeName: '',
    flag: '',
    isDefault: false,
    isRTL: false
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const queryClient = useQueryClient();

  // Fetch languages
  const { data: languages, isLoading, refetch } = useQuery({
    queryKey: ['languages'],
    queryFn: async () => {
      const response = await apiService.getLanguages();
      return response.data as Language[];
    }
  });

  // Create language mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiService.createLanguage(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['languages'] });
      setSnackbar({ open: true, message: 'Language created successfully', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to create language', severity: 'error' });
    }
  });

  // Update language mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await apiService.updateLanguage(id, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['languages'] });
      setSnackbar({ open: true, message: 'Language updated successfully', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to update language', severity: 'error' });
    }
  });

  // Delete language mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.deleteLanguage(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['languages'] });
      setSnackbar({ open: true, message: 'Language deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      setSelectedLanguage(null);
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to delete language', severity: 'error' });
    }
  });

  // Set default language mutation
  const setDefaultMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.setDefaultLanguage(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['languages'] });
      setSnackbar({ open: true, message: 'Default language updated successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to set default language', severity: 'error' });
    }
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await apiService.toggleLanguageActive(id, isActive);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['languages'] });
      setSnackbar({ open: true, message: 'Language status updated successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to update language status', severity: 'error' });
    }
  });

  const handleOpenDialog = (language?: Language) => {
    if (language) {
      setSelectedLanguage(language);
      setFormData({
        name: language.name,
        code: language.code,
        nativeName: language.nativeName,
        flag: language.flag || '',
        isDefault: language.isDefault,
        isRTL: language.isRTL ?? false
      });
    } else {
      setSelectedLanguage(null);
      setFormData({
        name: '',
        code: '',
        nativeName: '',
        flag: '',
        isDefault: false,
        isRTL: false
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLanguage(null);
    setFormData({
      name: '',
      code: '',
      nativeName: '',
      flag: '',
      isDefault: false,
      isRTL: false
    });
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.code || !formData.nativeName) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    if (selectedLanguage) {
      updateMutation.mutate({ id: selectedLanguage._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = () => {
    if (selectedLanguage) {
      deleteMutation.mutate(selectedLanguage._id);
    }
  };

  const handleSetDefault = (id: string) => {
    setDefaultMutation.mutate(id);
  };

  const handleToggleActive = (language: Language) => {
    toggleActiveMutation.mutate({ id: language._id, isActive: !language.isActive });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Language Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Language
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Native Name</TableCell>
                  <TableCell>Flag</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>RTL</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {languages && languages.length > 0 ? (
                  languages.map((language) => (
                    <TableRow key={language._id}>
                      <TableCell>{language.name}</TableCell>
                      <TableCell>
                        <Chip label={language.code} size="small" />
                      </TableCell>
                      <TableCell>{language.nativeName}</TableCell>
                      <TableCell>{language.flag || '-'}</TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={language.isActive}
                              onChange={() => handleToggleActive(language)}
                              size="small"
                            />
                          }
                          label={language.isActive ? 'Active' : 'Inactive'}
                        />
                      </TableCell>
                      <TableCell>
                        {language.isDefault ? (
                          <Chip icon={<StarIcon />} label="Default" color="primary" size="small" />
                        ) : (
                          <Tooltip title="Set as default">
                            <IconButton
                              size="small"
                              onClick={() => handleSetDefault(language._id)}
                              disabled={setDefaultMutation.isPending}
                            >
                              <StarIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {language.isRTL ? (
                          <Chip label="RTL" color="secondary" size="small" />
                        ) : (
                          <Chip label="LTR" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(language)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedLanguage(language);
                            setDeleteDialogOpen(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary">No languages found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedLanguage ? 'Edit Language' : 'Add Language'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Language Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              required
              fullWidth
              helperText="e.g., en, en-US, es, es-ES"
              disabled={!!selectedLanguage}
            />
            <TextField
              label="Native Name"
              value={formData.nativeName}
              onChange={(e) => setFormData({ ...formData, nativeName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Flag (Optional)"
              value={formData.flag}
              onChange={(e) => setFormData({ ...formData, flag: e.target.value })}
              fullWidth
              helperText="Flag emoji or code"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
              }
              label="Set as default language"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isRTL}
                  onChange={(e) => setFormData({ ...formData, isRTL: e.target.checked })}
                />
              }
              label="Right-to-Left (RTL) Language"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {selectedLanguage ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Language</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedLanguage?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleteMutation.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LanguageManagement;

